import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@crowdfpl/ui-web/sheet';
import { Tooltip, TooltipContent, TooltipPortal, TooltipProvider, TooltipTrigger } from '@crowdfpl/ui-web/tooltip';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { suppressDuelInteractions } from '@/helpers/duelInteractionGuard';
import { useIsMobile } from '@/hooks/utils/use-mobile';
import type { DuelPlayer } from './types';

const DUEL_CANCELLED_MESSAGE = 'If a player gets 0 minutes, the duel is cancelled.';

const FPL_STATUS_LABELS: Record<string, string> = {
  a: 'Available',
  d: 'Doubtful',
  i: 'Injured',
  s: 'Suspended',
  u: 'Unavailable',
  n: 'Not in squad',
};

function getStatusLabel(status: string | null | undefined): string {
  if (!status) return '';
  return FPL_STATUS_LABELS[status] ?? status;
}

function getTooltipLine1(pct: number, status: string | null | undefined, news: string | null | undefined): string {
  const base = `${pct}% chance of playing`;
  const extra = news?.trim() || getStatusLabel(status);
  return extra ? `${base} · ${extra}` : base;
}

function getAvailabilityColorClasses(pct: number): { colorClass: string; bgClass: string } {
  if (pct >= 75) return { colorClass: 'text-yellow-500 dark:text-yellow-400', bgClass: 'bg-yellow-500/15' };
  if (pct >= 25) return { colorClass: 'text-orange-500 dark:text-orange-400', bgClass: 'bg-orange-500/15' };
  return { colorClass: 'text-red-500 dark:text-red-400', bgClass: 'bg-red-500/15' };
}

export interface AvailabilityBadgeProps {
  player: Pick<DuelPlayer, 'availability_pct' | 'availability_status' | 'availability_news'>;
  position: 'left' | 'right';
  /** On mobile: render inline in the Vote slot (no absolute positioning) */
  inline?: boolean;
}

export function AvailabilityBadge({ player, position, inline = false }: AvailabilityBadgeProps) {
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = useState(false);

  const pct = player.availability_pct;
  if (pct === null || pct === undefined || pct === 100) return null;

  const { colorClass, bgClass } = getAvailabilityColorClasses(pct);

  const line1 = getTooltipLine1(pct, player.availability_status, player.availability_news);
  const tooltipContent = (
    <div className="text-xs">
      <div>{line1}</div>
      <div className="mt-1 text-muted-foreground">{DUEL_CANCELLED_MESSAGE}</div>
    </div>
  );

  const posClass = position === 'left' ? 'right-1.5 top-8' : 'left-1.5 top-8';

  const handleBadgeClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (isMobile) setSheetOpen(true);
  };

  const handleSheetOpenChange = (open: boolean) => {
    setSheetOpen(open);
    if (!open) suppressDuelInteractions();
  };

  const badgeButton = (
    <button
      type="button"
      className={`flex h-4 w-4 min-h-0 min-w-0 items-center justify-center rounded-full p-0 ${bgClass} shrink-0 touch-manipulation ${inline ? 'cursor-pointer' : 'cursor-help'}`}
      aria-label="Player availability"
      onClick={handleBadgeClick}
      onPointerDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
    >
      <AlertTriangle className={`h-2.5 w-2.5 ${colorClass}`} />
    </button>
  );

  const wrapper = (
    <div
      className={inline
        ? 'inline-flex h-4 w-4 max-w-4 max-h-4 flex-shrink-0 items-center justify-center touch-manipulation'
        : `absolute ${posClass} z-30`}
    >
      {isMobile ? (
        badgeButton
      ) : (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className={inline ? 'inline-flex size-6 flex-shrink-0 items-center justify-center' : undefined}>
                {badgeButton}
              </div>
            </TooltipTrigger>
            <TooltipPortal container={typeof document !== 'undefined' ? document.body : undefined}>
              <TooltipContent side="top" className="max-w-[220px] z-[99999]">
                {tooltipContent}
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <>
        {wrapper}
        <Sheet open={sheetOpen} onOpenChange={handleSheetOpenChange}>
          <SheetContent side="bottom" className="rounded-t-xl max-h-[70vh] pb-safe">
            <SheetHeader className="mb-3">
              <SheetTitle className="text-sm font-semibold text-foreground">
                Availability
              </SheetTitle>
            </SheetHeader>
            <div className="text-xs">
              <div className="text-foreground">{line1}</div>
              <div className="mt-1 text-muted-foreground">{DUEL_CANCELLED_MESSAGE}</div>
            </div>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return wrapper;
}
